import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'

type CollapsibleSectionCardProps = {
  icon: React.ReactNode
  title: string
  // Small muted text after the title, e.g. the briefing's date.
  subtitle?: string
  count: number
  countClassName: string
  // Optional content rendered between the count and the chevron — e.g.
  // a status dot + "New"/"Review" badge in the Results section.
  headerExtras?: React.ReactNode
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}

export function CollapsibleSectionCard({
  icon,
  title,
  subtitle,
  count,
  countClassName,
  headerExtras,
  open,
  onToggle,
  children,
}: CollapsibleSectionCardProps) {
  return (
    <div
      className={[
        'flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm',
        // When open: grow to fill available space (flex-1) but cap at content
        // height (max-h-max). A small list hits its content cap and stops
        // growing, freeing the leftover space for the sibling card.
        open ? 'max-h-max min-h-0 flex-1' : 'shrink-0',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full shrink-0 items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          {icon}
          <span className="text-sm font-semibold text-gray-900">
            {title}
            {subtitle && (
              <small className="ml-1.5 text-xs font-medium text-gray-500">{subtitle}</small>
            )}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {count > 0 && <span className={`text-sm font-semibold ${countClassName}`}>{count}</span>}
          {headerExtras}
          {open ? (
            <IconChevronUp size={16} className="text-gray-500" />
          ) : (
            <IconChevronDown size={16} className="text-gray-500" />
          )}
        </div>
      </button>
      {open && (
        <div className="flex min-h-0 flex-1 flex-col border-t border-gray-100">{children}</div>
      )}
    </div>
  )
}
